import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  availableDevicesQueryOptions,
  siteQueryOptions,
  studyQueryOptions,
  subjectQueryOptions,
} from '../../../queryOptions';
import { Device, DeviceEntityType, Site, Study } from '../../../models';
import {
  CheckboxFilterItem,
  CheckboxFilterSection,
} from '../../table/DynamicTable';
import { EntityType } from 'adc-sirius-module-common';
import i18n from '../../../utilities/i18n';

export type DeviceAssignmentRecord = Pick<
  Device,
  'key' | 'appName' | 'deviceId' | 'phoneModel' | 'studyKey' | 'siteKey'
> & {
  studyName: string;
  siteName: string;
};

export type DeviceAssignmentTableData = {
  data: DeviceAssignmentRecord[];
  filters: CheckboxFilterSection[];
};
type Props = {
  entityType: DeviceEntityType;
  entityKey: string;
};

/**
 * Custom hook used by the AssignDeviceModal to retrieve the sorted device assignment data
 * and filter configuration used by the DynamicTable.
 * @param {DeviceEntityType} entityType - the type of entity for which devices are being assigned
 * @param {string} entityKey - the unique key of the entity for which devices are being assigned
 * @returns {DeviceAssignmentTableData} - the device assignment records and filter configuration for the DynamicTable
 */
export const useDeviceAssignmentRecords = ({
  entityType,
  entityKey,
}: Props): DeviceAssignmentTableData => {
  const studyByKey = useMemo(() => new Map<string, Study | undefined>(), []);
  const siteByKey = useMemo(() => new Map<string, Site | undefined>(), []);
  const subject = useQuery(
    subjectQueryOptions({
      key: entityType === EntityType.StudySubject ? entityKey : undefined,
    }),
  );

  const devices = useQuery(availableDevicesQueryOptions({}));

  if (devices.isSuccess && devices.data.length) {
    devices.data.forEach(({ studyKey, siteKey }) => {
      if (studyKey) {
        studyByKey.set(studyKey, undefined);
      }

      if (siteKey) {
        siteByKey.set(siteKey, undefined);
      }
    });

    if (entityType === EntityType.Study) {
      studyByKey.set(entityKey, undefined);
    } else if (entityType === EntityType.StudySite) {
      siteByKey.set(entityKey, undefined);
    }
  }

  const studies = useQueries({
    queries: devices.isSuccess
      ? studyByKey
          .keys()
          .toArray()
          .map((key) => studyQueryOptions({ key }))
      : [],
    combine: (results) => {
      results
        .flatMap((result) => result.data)
        .forEach((study) => {
          if (study) {
            studyByKey.set(study.key, study);
          }
        });
      return {
        data: studyByKey,
        pending: results.some((result) => result.isPending),
      };
    },
  });

  const sites = useQueries({
    queries: devices.isSuccess
      ? siteByKey
          .keys()
          .toArray()
          .map((key) => siteQueryOptions({ key }))
      : [],
    combine: (results) => {
      results
        .flatMap((result) => result.data)
        .forEach((site) => {
          if (site) {
            siteByKey.set(site.key, site);
          }
        });
      return {
        data: siteByKey,
        pending: results.some((result) => result.isPending),
      };
    },
  });

  const data = useMemo(() => {
    const records: DeviceAssignmentRecord[] = [];

    // Maps an index in the records array to a priority. Used for more efficient sorting.
    const mappedRecords: { index: number; priority: number }[] = [];

    if (!studies.pending && !sites.pending) {
      devices.data?.forEach(
        ({ key, appName, deviceId, phoneModel, studyKey, siteKey }, index) => {
          // Map device data to table record
          records.push({
            key,
            appName,
            deviceId,
            phoneModel,
            studyKey: studyKey || '',
            siteKey: siteKey || '',
            studyName: (studyKey && studyByKey.get(studyKey)?.name) || '',
            siteName: (siteKey && siteByKey.get(siteKey)?.name) || '',
          });

          // Get device priority based on assignment
          let priority;

          if (
            // Devices assigned to the current entity
            (entityType === EntityType.Study && studyKey === entityKey) ||
            (entityType === EntityType.StudySite && siteKey === entityKey)
          ) {
            priority = 4;
          } else if (
            // Devices assigned to a parent of the current entity
            (entityType === EntityType.StudySubject &&
              siteKey === subject.data?.siteKey) ||
            (entityType === EntityType.StudySite &&
              studyKey === siteByKey.get(entityKey)?.studyKey)
          ) {
            priority = 3;
          } else if (
            entityType === EntityType.StudySubject &&
            subject.data?.siteKey &&
            studyKey === siteByKey.get(subject.data.siteKey)?.studyKey
          ) {
            // Devices assigned to a grandparent of the current entity
            priority = 2;
          } else if (!studyKey && !siteKey) {
            // Unassigned devices
            priority = 1;
          } else {
            // Devices assigned to any other entity
            priority = 0;
          }

          mappedRecords.push({ index, priority });
        },
      );

      // Sort priority mapping
      mappedRecords.sort((a, b) => {
        if (a.priority < b.priority) {
          return 1;
        }

        if (a.priority > b.priority) {
          return -1;
        }

        return 0;
      });

      // Apply priority mapping to row data
      return mappedRecords.map(({ index }) => records[index]);
    }

    return records;
  }, [
    studies.pending,
    sites.pending,
    devices.data,
    studyByKey,
    siteByKey,
    entityType,
    entityKey,
    subject.data,
  ]);

  const filters = useMemo(() => {
    if (!studies.pending && !sites.pending && devices.data?.length) {
      const siteOptions: CheckboxFilterItem[] = [];

      siteByKey
        .entries()
        .toArray()
        .forEach(([key, site]) => {
          if (
            key === entityKey &&
            !devices.data?.find((device) => device.siteKey === key)
          ) {
            return;
          }

          siteOptions.push({
            id: key,
            label: site?.name || '',
            value: false,
          });
        });

      siteOptions.push({
        id: '',
        label: i18n.t('Modal_AssignDevice.unassigned'),
        value: false,
      });

      const studyOptions: CheckboxFilterItem[] = [];

      studyByKey
        .entries()
        .toArray()
        .forEach(([key, study]) => {
          if (
            key === entityKey &&
            !devices.data?.find((device) => device.studyKey === key)
          ) {
            return;
          }

          studyOptions.push({
            id: key,
            label: study?.name || '',
            value: false,
          });
        });

      studyOptions.push({
        id: '',
        label: i18n.t('Modal_AssignDevice.unassigned'),
        value: false,
      });

      return [
        {
          id: 'siteKey',
          label: i18n.t('Entity.site'),
          items: siteOptions,
        },
        {
          id: 'studyKey',
          label: i18n.t('Entity.study'),
          items: studyOptions,
        },
      ];
    } else {
      return [];
    }
  }, [
    devices.data,
    entityKey,
    studies.pending,
    sites.pending,
    siteByKey,
    studyByKey,
  ]);

  return {
    data,
    filters,
  };
};
